import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NFIdentity, WorkflowTemplates } from '@nibras/core/common';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, EMPTY, firstValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, tap, withLatestFrom } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { CommonService } from './common.service';
import { IDialogService } from './dialog.interface';
import { EventsService } from './events.service';
import { HttpService } from './http.service';
import { ComponentMetadataModel, ComponentSettingsModel, dcExternalParameter, Widget } from './model';
import {
  AppAction,
  AppCache,
  AppQueryParams,
  AppRouteStateParams,
  AppTagTrigger,
  CachedAppMenuDetails,
  CommonProgressStatus,
  DeeplinkAppParams,
  DeeplinkParams,
  DefaultAppAction,
  DefaultAppActionType,
  SidebarTabAction
} from './model/app-cache.model';
import { App, WidgetObjective } from './model/app.model';
import { HierarchyTagWrapper } from './model/common.interfaces';
import { AppConstants } from './model/constants.model';
import { dcTagFlatTree } from './model/dc-tag-flat-tree.model';
import { dcTag } from './model/dcTag.model';
import { Dictionary } from './model/dictionary';

@Injectable()
export class AppCacheService extends EventsService {
  private selectedAppId: number;
  private previousAppId: number;

  private _refreshTaskViewer: boolean;
  public defaultAppAction: DefaultAppAction;
  public defaultAppActionProcessId: number;
  public isFromWorkflowApp: boolean;
  private appIdsToRefresh: number[] = [];
  public closeAppFromSidebar: (appId: number) => void;

  public get refreshTaskViewer(): boolean {
    return this._refreshTaskViewer;
  }

  public set refreshTaskViewer(value: boolean) {
    const index =
      this.appIdsToRefresh && this.appIdsToRefresh.indexOf(WorkflowTemplates.TaskDashBoard);
    if (index === -1 && value) {
      this.appIdsToRefresh.push(WorkflowTemplates.TaskDashBoard);
    } else if (!value && index > -1) {
      this.appIdsToRefresh.splice(index);
    }

    this._refreshTaskViewer = value;
  }

  public appCaches: AppCache[] = [];

  private selectedAppCache?: BehaviorSubject<AppCache>;
  private selectedAppCache$: Observable<AppCache>;

  private cachedAppMenuDetails?: BehaviorSubject<CachedAppMenuDetails>;
  public cachedAppMenuDetails$: Observable<CachedAppMenuDetails>;

  constructor(
    public router: Router,
    public commonService: CommonService,
    public httpService: HttpService,
    private cacheService: CacheService
  ) {
    super();

    this.selectedAppCache = new BehaviorSubject(null);
    this.cachedAppMenuDetails = new BehaviorSubject(null);
    this.selectedAppCache$ = this.selectedAppCache.asObservable();
    this.cachedAppMenuDetails$ = this.cachedAppMenuDetails.asObservable();
  }

  public getSelectedAppProgress() {
    const app = this.getSelectedApp();
    if (app) return app.getSelectedAppProgress();
    else return of(null);
  }

  public toggleAppProgress(show = true, showBackDrop = true, appId?: number) {
    const progressStatus: CommonProgressStatus = { show, showBackDrop };
    const app = appId ? this.findCachedApp(appId) : this.getSelectedApp();

    if (app) {
      app.toggleAppProgress(progressStatus);
      return;
    }

    const selectedApp = this.getSelectedApp();
    const linkedApp = appId && selectedApp?.linkedApps?.find(linkApp => linkApp.appId === appId);
    linkedApp?.toggleAppProgress(progressStatus);
  }

  public getCachedApps() {
    return this.appCaches;
  }

  public createNewAppCache(appId: number): AppCache {
    return new AppCache(appId);
  }

  public onHighlightElementTagChange() {
    const app = this.getSelectedApp();
    if (app) return app.onHighlightElementTagChange();
    else return of(null);
  }

  public onSelectedScopeTagChange() {
    const app = this.getSelectedApp();
    if (app) return app.onSelectedScopeTagChange();
    else return of(null);
  }

  public onSidebarTagsChanges(isNestedApp = false): Observable<dcTag[]> {
    const app = this.getSelectedApp();
    if (app) return app.onSidebarTagsChanges(isNestedApp);
    else return of([]);
  }

  public onSidebarTagsObjChanges(isNestedApp = false) {
    const app = this.getSelectedApp();
    if (app) return app.onSidebarTagsObjChanges(isNestedApp);
    else return EMPTY;
  }

  public dispatchHighlightElementTag(tag: dcTag, tagetAppId?: number) {
    const app = tagetAppId ? this.findCachedApp(tagetAppId) : this.getSelectedApp();
    if (!app) return;

    app.dispatchHighlightElementTag(tag);
  }

  public dispatchSidebarTags(
    tags: dcTag[],
    tagetAppId?: number,
    forceRefresh = false,
    dispatchToNestedApp = false,
    isDefaultProfileTag = false,
    appTagTrigger = AppTagTrigger.Default
  ) {
    const app = tagetAppId ? this.findCachedApp(tagetAppId) : this.getSelectedApp();
    if (!app) return;

    if (!app.appConfig) {
      console.info('App config not found, tag dispatch deffered until config load');
      app.setTriggerTagsAfterLoad(tags, null, appTagTrigger);
    } else {
      app.dispatchSidebarTags(
        tags,
        forceRefresh,
        dispatchToNestedApp,
        isDefaultProfileTag,
        appTagTrigger
      );
    }
  }

  public reloadSidebarTags(isNestedApp = false) {
    const app = this.getSelectedApp();
    if (!app) return;

    const tags = app.getSidebarTagsValue(isNestedApp);
    app.dispatchSidebarTags(tags, true, isNestedApp, false);
  }

  public onHierarchyChanges(appId?: number): Observable<HierarchyTagWrapper> {
    const app = appId ? this.findCachedApp(appId) : this.getSelectedApp();
    if (app) {
      return app.onHierarchyChanges();
    }
    return EMPTY;
  }

  public changeAppHierarchy(
    hierarchy: HierarchyTagWrapper,
    forceTags = false,
    tagetAppId?: number
  ) {
    const app = tagetAppId ? this.findCachedApp(tagetAppId) : this.getSelectedApp();
    if (!app) return;

    app.changeAppHierarchy(hierarchy, forceTags);
  }

  public shouldReloadApp(appId: number) {
    return this.appIdsToRefresh && this.appIdsToRefresh.indexOf(appId) > -1;
  }

  public reloadProcessElement(isNestedApp = false) {
    const app = this.getSelectedApp();
    if (!app) return;

    this.broadcast(AppConstants.ACTIONS.RELOAD_PROCESS_ELEMENT, app);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public onReloadProcessElement(isNestedApp = false, listener: Function) {
    return this.on(AppConstants.ACTIONS.RELOAD_PROCESS_ELEMENT, listener);
  }

  public dispatchSelectedScopeTag(tag: dcTag) {
    const app = this.getSelectedApp();
    if (!app) return;
    app.dispatchSelectedScopeTag(tag);
  }

  public setSidebarTabSelection(sidebarTabAction: SidebarTabAction) {
    const app = this.getSelectedApp();
    if (!app) return;
    app.setSidebarTabSelection(sidebarTabAction);
  }

  public addToCache(app: AppCache) {
    if (this.appCaches.indexOf(app) === -1) {
      this.appCaches.push(app);
      return true;
    }
  }

  public dispatchWidgetDate(externalparameter: dcExternalParameter) {
    const app = this.getSelectedApp();
    if (!app) return;
    app.dispatchWidgetDate(externalparameter);
  }

  public onWidgetDateChange() {
    const app = this.getSelectedApp();
    if (!app) return;
    return app.getwidgetDateObservable();
  }

  public dispatchCreateTask(elementName: string, attachedProcess: string) {
    const app = this.getSelectedApp();
    if (!app) return;
    app.dispatchCreateTask(elementName, attachedProcess);
  }

  public onCreateTask() {
    const app = this.getSelectedApp();
    if (!app) return;
    return app.getCreateTaskObservable();
  }

  public selectApp(app: AppCache, forceTags?: boolean) {
    const isNew = this.addToCache(app);

    const previousApp = this.getSelectedApp();

    this.previousAppId = app.appId != this.selectedAppId ? this.selectedAppId : this.previousAppId;
    this.selectedAppId = app.appId;

    if (app.appConfig) {
      this.appConfigLoaded(
        app.appConfig,
        app.processName,
        app.processParams && app.processParams.Id
      );
    }

    app.lastAccessedOn = new Date();
    if (previousApp) {
      previousApp.appSelectionChange.next({ selected: false });
    }

    app.appSelectionChange.next({ isNewlyOpenedApp: isNew, selected: true });

    if (app.appId === WorkflowTemplates.TaskDashBoard && this.refreshTaskViewer) {
      this.reloadSidebarTags();
    }

    if (!isNew && this.isFromWorkflowAppPage(previousApp) && !app.processParams) {
      //This is to fix: ProcessRequest repeats while switching from Workflow / DataExplorer apps
      //to a group app which is already cached.
      this.isFromWorkflowApp = true;
    }

    const hasDefaultAppAction = this.defaultAppAction ? true : false;
    if (
      app.processParams &&
      this.defaultAppAction &&
      this.defaultAppActionProcessId === app.processParams.Id
    ) {
      app.onDefaultAppActionChange.next(this.defaultAppAction);
      this.setDefaultAppAction(null, null);
    }

    if (
      previousApp &&
      !previousApp.processAppId &&
      app.appId != AppConstants.STATIC_WORKFLOW_APP_ID
    ) {
      let appTagTrigger = AppTagTrigger.Default;
      let tagsToSend: dcTag[];

      if (this.defaultAppAction?.action === DefaultAppActionType.DeepLinkApp) {
        const deepLinkParams = this.defaultAppAction.param as DeeplinkParams;
        if (deepLinkParams) {
          tagsToSend = deepLinkParams.tags;
          appTagTrigger = AppTagTrigger.Deeplink;
        }
        app.onDefaultAppActionChange.next(this.defaultAppAction);
        this.setDefaultAppAction(null, null);
      } else {
        tagsToSend = previousApp.getSidebarTagsValue();
      }

      const canTriggerSidebarTagsWhenDefaultAppAction =
        forceTags && tagsToSend && hasDefaultAppAction && app && app.appConfig;

      if ((isNew || canTriggerSidebarTagsWhenDefaultAppAction) && previousApp?.hierarchyData) {
        app.changeAppHierarchy(cloneDeep(previousApp?.hierarchyData));
      }

      const selectedAppTags = app.getSidebarTagsValue();

      if (
        (!selectedAppTags || !selectedAppTags.length) &&
        tagsToSend &&
        (!forceTags || (forceTags && isNew))
      ) {
        app.setTriggerTagsAfterLoad(
          cloneDeep(tagsToSend),
          previousApp.hierarchyData && cloneDeep(previousApp.hierarchyData.profileTagList),
          appTagTrigger
        );
      } else if (canTriggerSidebarTagsWhenDefaultAppAction) {
        this.dispatchSidebarTags(tagsToSend, app.appId, true, false, false, appTagTrigger);
      }
    }

    //remove previous app if its unauthorized.
    if (previousApp && !previousApp.isAppAuthorized()) {
      this.appCaches.splice(this.appCaches.indexOf(previousApp), 1);
    }
  }

  public isFromWorkflowAppPage(previousApp: AppCache) {
    return (
      previousApp &&
      ((previousApp.processAppId ||
        !previousApp.appConfig ||
        !previousApp.appConfig.IsElementSelectionRequired) as boolean)
    );
  }

  public closeApp(appId: number, selectNext = true, processId?: number) {
    const app = this.findCachedApp(appId, processId);
    if (app) {
      app.appSelectionChange.next({ selected: false });

      const index = this.appCaches.indexOf(app);
      this.appCaches.splice(index, 1);

      if (selectNext && this.selectedAppId == app.appId) this.selectNextApp(index);
    }
  }

  public selectPreviousApp() {
    const previousApp = this.findCachedApp(this.previousAppId);
    this.navigateToAppCache(previousApp);
  }

  public selectNextApp(index: number) {
    if (this.previousAppId) {
      this.selectPreviousApp();
    } else {
      let nextApp: AppCache;
      if (this.appCaches.length > index - 1) {
        nextApp = this.appCaches[index - 1];
      } else if (this.appCaches.length > index) {
        nextApp = this.appCaches[index];
      }

      this.navigateToAppCache(nextApp);
    }
  }

  private navigateToAppCache(app: AppCache) {
    if (app) {
      this.selectApp(app);
      this.commonService.navigateToApp(app.appId, app.widgetId);
    } else {
      this.appCaches.splice(0, this.appCaches.length);
      this.selectedAppId = null;
      this.previousAppId = null;
      this.commonService.navigateToHome();
    }
  }

  public navigateToApp(
    appId: number,
    widgetId?: number,
    //TODO: Need to remove Dictionary<string | number | boolean> from here, all should use typed params.
    params?: Dictionary<string | number | boolean> | AppQueryParams,
    deepLinkParams?: dcTag[] | DeeplinkParams
  ) {
    let appCallback: Subject<AppAction>;
    if (deepLinkParams) {
      appCallback = this.setAppAction(deepLinkParams);
      //Timeout is there
      setTimeout(() => {
        this.commonService.navigateToApp(appId, widgetId, params);
      }, 100);
    } else {
      this.commonService.navigateToApp(appId, widgetId, params);
    }
    return appCallback;
  }

  async navigateToDxManager(
    dialog: IDialogService,
    confirmationRequired?: boolean,
    deepLinkParams?: dcTag[] | DeeplinkParams
  ) {
    let accept = true;
    if (confirmationRequired) {
      accept = await firstValueFrom(
        dialog.showConfirm({
          title: 'Dx Manager',
          message: 'Would you like to navigate to Dx Manager?',
          acceptButton: 'Yes',
          cancelButton: 'No'
        })
      ).catch((): boolean | null => null);
    }

    if (accept) {
      return this.navigateToApp(AppConstants.DX_MANAGER_APP_ID, null, null, deepLinkParams);
    }
  }

  public setAppAction(deepLinkParams: dcTag[] | DeeplinkParams) {
    const param = Array.isArray(deepLinkParams)
      ? ({ tags: deepLinkParams } as DeeplinkParams)
      : deepLinkParams;

    param.appCallback = new Subject<AppAction>();

    //Setting default Source to Element.
    param.tags?.forEach(tag => {
      if (!tag.hasOwnProperty('SourceClassID')) {
        tag.SourceClassID = NFIdentity.Element;
      }
    });

    const appAction: DefaultAppAction = {
      action: DefaultAppActionType.DeepLinkApp,
      param
    };
    this.setDefaultAppAction(appAction);
    return param.appCallback;
  }

  public findCachedApp(appId: number, processInstanceId?: number, processId?: number) {
    if (processInstanceId) {
      if (processId !== undefined && processId !== null) {
        return this.appCaches.find(
          app =>
            (app.processAppId === appId || app.appId === appId) &&
            app.processParams &&
            app.processParams.Id == processInstanceId &&
            app.processParams.ProcessID == processId
        );
      }

      return this.appCaches.find(
        app =>
          app.processAppId === appId &&
          app.processParams &&
          app.processParams.Id == processInstanceId
      );
    }

    return this.appCaches.find(app => app.appId === appId);
  }

  public getSelectedApp<
    TAppConfig extends App = App,
    TAppRouteStateParams extends AppRouteStateParams = AppRouteStateParams
  >() {
    return this.findCachedApp(this.selectedAppId) as AppCache<TAppConfig, TAppRouteStateParams>;
  }

  public getSelectedAppCache() {
    // const app = this.getSelectedApp();
    // if (!app) return EMPTY;

    return this.selectedAppCache$;
  }

  public getSelectedAppConfigAndTitle() {
    // const app = this.getSelectedApp();
    // if (!app) return EMPTY;

    return this.cachedAppMenuDetails$.pipe(withLatestFrom(this.selectedAppCache$));
  }

  public appConfigLoaded(
    appConfig: App,
    processName?: string,
    processId?: number,
    isNewlyOpened?: boolean
  ) {
    if (!appConfig && !processName) return;

    let appCache: AppCache;
    if (appConfig) {
      appCache = this.findCachedApp(appConfig.Id, processId);
      if (isNewlyOpened) {
        this.setAppConfig(appConfig, appCache);
        //Copy previous app's to new app.
        if (appCache) {
          this.copyCacheData(appCache);
          //This is required to notify all the events subscribed in App and its child components.
          appCache.reloadAppHierarchy(this.selectedAppCache?.getValue());
        }
      }
      this.selectedAppCache.next(appCache);
      this.triggerTagsToAfterLoad(appCache);
    } else {
      this.selectedAppCache.next(null);
    }

    if (appConfig && appConfig.TriggerDefaultRequest) {
      this.dispatchSidebarTags(null, appConfig.Id, true);
    }

    this.updateProcessName(processName, appCache);
  }

  public setAppConfig(appConfig: App, appCache: AppCache) {
    const supportedType = new dcTag();
    this.setAppSupportedTypeTag(appConfig, supportedType);

    if (appCache) {
      appCache.appConfig = appConfig;
    }
  }

  private triggerTagsToAfterLoad(app: AppCache) {
    if (app && (app.tagsToTriggerAfterLoad?.tags || app.previousAppProfileTagList)) {
      const hasDefaultAppAction =
        app.onDefaultAppActionChange?.value ||
        app.tagsToTriggerAfterLoad?.appTagTrigger === AppTagTrigger.Deeplink;
      const isGroupAppAndNoDefaultAppAction =
        app.appConfig &&
        app.appConfig.Is_Group &&
        app.previousAppProfileTagList &&
        !hasDefaultAppAction;
      const tagsToSend = isGroupAppAndNoDefaultAppAction
        ? app.previousAppProfileTagList
        : app.tagsToTriggerAfterLoad?.tags;

      app.dispatchSidebarTags(
        tagsToSend,
        false,
        false,
        //If its group app previous tags are expected to be Heiarchy tags. So need to send isDefaultProfileTag as true.
        isGroupAppAndNoDefaultAppAction,
        app.tagsToTriggerAfterLoad?.appTagTrigger
      );
      app.tagsToTriggerAfterLoad = null;
      app.previousAppProfileTagList = null;
    }
  }

  private setAppSupportedTypeTag(appConfig: App, supportedType: dcTag) {
    if (
      appConfig.SupportedType &&
      (!appConfig.supportedTypeTag ||
        !appConfig.supportedTypeTag ||
        appConfig.SupportedType != appConfig.supportedTypeTag.TagID)
    ) {
      //TODO: Need to have separate value for 'Entity' tag name for Data explorer label
      supportedType.TagName =
        appConfig.AppName == 'Data Explorer' ? 'Entity' : NFIdentity[appConfig.SupportedType];
      supportedType.ElementTypeID = appConfig.SupportedType;
    }
    appConfig.supportedTypeTag = supportedType;
  }

  private copyCacheData(app: AppCache) {
    if (app.appConfig && !app.appConfig.Is_Group) {
      const apps = this.getCachedApps();

      //finding last browsed Single element App.
      const matchingApps = apps.filter(
        cacheApp =>
          cacheApp.appId != app.appId &&
          cacheApp.appConfig &&
          !cacheApp.appConfig.Is_Group &&
          app.appConfig.SupportedType == cacheApp.appConfig.SupportedType &&
          cacheApp.elementBrowserData
      );

      if (matchingApps && matchingApps.length) {
        const matchingApp = matchingApps.reduce((a, b) =>
          a.lastAccessedOn > b.lastAccessedOn && a.appId != app.appId ? a : b
        );

        if (matchingApp && matchingApp.elementBrowserData) {
          app.elementBrowserData = cloneDeep(matchingApp.elementBrowserData);
        }
      }
    }
  }

  public setAppUserPermission(readOnly: boolean) {
    const appCache = this.getSelectedApp();
    if (appCache) appCache.isReadOnly = readOnly;
  }

  public getAppUserPermission() {
    const appCache = this.getSelectedApp();
    return appCache?.isReadOnly;
  }

  public setHierarchyChangeConfirmation(confirmationFn: () => boolean) {
    const appCache = this.getSelectedApp();
    if (appCache) appCache.topologyHierachyConfirmFn = confirmationFn;
  }

  public getHierarchyChangeConfirmation() {
    const appCache = this.getSelectedApp();
    return appCache?.topologyHierachyConfirmFn;
  }

  public getSelectedAppHierarchyTag(appId: number): Observable<HierarchyTagWrapper> {
    return this.onHierarchyChanges().pipe(
      filter(() => {
        const appCache = this.getSelectedApp();
        return appCache?.appId === appId;
      })
    );
  }

  public updateProcessName(processName: string, appCache: AppCache) {
    let processNameToUpdate: string;
    if (
      this.router.url.includes('/' + AppConstants.APPLICATION_DASHBOARD_ROUTE) ||
      !this.selectedAppId ||
      this.selectedAppId == AppConstants.STATIC_WORKFLOW_APP_ID
    ) {
      processNameToUpdate = processName;
    } else {
      processNameToUpdate = null;
    }

    if (appCache) appCache.processName = processNameToUpdate;
    this.cachedAppMenuDetails.next({ processName: processNameToUpdate, appCache });
  }

  public getLastTag(tags: dcTag[], isGroup: boolean) {
    const tagList =
      tags &&
      tags.filter(
        tag =>
          tag.isRemovable &&
          (!tag.hasOwnProperty('expandable') || !(tag as dcTagFlatTree).expandable)
      );
    if (tagList && tagList.length) {
      return tagList[tagList.length - 1];
    }

    if (isGroup) {
      const tagListGrp =
        tags &&
        tags.filter(tag => tag.hasOwnProperty('expandable') && (tag as dcTagFlatTree).expandable);
      if (tagListGrp && tagListGrp.length) {
        return tagListGrp[tagListGrp.length - 1];
      }
    }

    return null;
  }

  public dispatchTagFromHomePage(tag: dcTag) {
    const app = this.getSelectedApp();
    if (!app) return;
    app.dispatchTagFromHomePage(tag);
  }

  public onTagFromHomePageChange() {
    const app = this.getSelectedApp();
    if (app) return app.onTagFromHomePageChange();
    else return of(null);
  }

  public setDefaultAppAction(defaultAppAction: DefaultAppAction, processId?: number) {
    this.defaultAppAction = defaultAppAction;
    this.defaultAppActionProcessId = processId;
  }

  public onDeepLinkParamsChanges<T extends DeeplinkAppParams>() {
    const app = this.getSelectedApp();
    if (app) {
      return app.onDeepLinkParamsChanges<T>();
    } else {
      return of(null);
    }
  }

  /**
   * Once API returns 403 Forbidden response,
   * this method will set the particular appId to UnAuthoriozed.
   *
   * @param appId
   * @param isAuthorized
   * @memberof AppCacheService
   */
  public setAuthorization(
    appId: number,
    isAuthorized: boolean,
    processInstanceId?: number,
    processId?: number
  ) {
    const app = this.findCachedApp(appId, processInstanceId, processId);
    if (app) {
      app.setAuthorization(isAuthorized);
    }
  }

  /**
   * Get App configuration based on AppID
   *
   * @param appID
   * @return
   * @memberof AppCacheService
   */
  getAppConfig(appID: number, processID?: number): Observable<App[]> {
    return this.httpService
      .get<App[]>(
        `App/Config/${appID}${processID ? `?processID=${processID}` : ''}`,
        null,
        appID.toString()
      )
      .pipe(
        catchError(err => {
          if (err && err.status === 403) {
            this.setAuthorization(appID, false);
          }

          const error = err.error ? err.error : err;
          return throwError(() => error);
        })
      );
  }

  /**
   * Get Lite version of Widget details. This is used for Lazy loading purpose.
   * Only essential details like WidgetName, Icon, Id, SupportedElements,
   * Order will be responsed by this method.
   *
   * @param appID
   * @param tags
   * @return
   * @memberof AppCacheService
   */
  public getAllWidgetsLite(
    appID: number,
    processID: number,
    tags: dcTag[],
    requiredWidgetAttributes: string
  ): Observable<Widget[]> {
    const serviceCall = this.httpService
      .post<Widget[]>(
        `App/${appID}/Widget/GetAll${requiredWidgetAttributes ? `?requiredWidgetAttributes=${requiredWidgetAttributes}` : ''
        }${processID ? `${requiredWidgetAttributes ? '&' : '?'}processID=${processID}` : ''}`,
        tags,
        `getAllWidgetsLite_${appID}`
      )
      .pipe(tap(this.parseWidgetsSupportedElements));

    const appCache = this.findCachedApp(appID);
    if (!appCache || !appCache.elementSelectorSelectedTagNode) {
      return serviceCall;
    }

    const key = this.getCacheKey('getAllWidgetsLite', appCache, appID, tags);
    return this.cacheService.get(
      key,
      serviceCall,
      7500000, //2 hrs
      true
    );
  }

  /**
   * Get Lite version of Widget details. This is used for Lazy loading purpose.
   * Only essential details like WidgetName, Icon, Id, SupportedElements,
   * Order will be responsed by this method.
   *
   * @param appID
   * @param tags
   * @return
   * @memberof AppCacheService
   */
  getAllWidgetObjectives(
    appID: number,
    tags: dcTag[],
    elementPaths: string[]
  ): Observable<WidgetObjective[]> {
    const serviceCall = this.httpService.post<WidgetObjective[]>(
      `App/${appID}/Widget/GetAllObjectives`,
      elementPaths,
      `getAllWidgetObjectives_${appID}`
    );

    const appCache = this.findCachedApp(appID);
    if (!appCache || !appCache.elementSelectorSelectedTagNode) {
      return serviceCall;
    }

    const key = this.getCacheKey('getAllWidgetObjectives', appCache, appID, tags);
    return this.cacheService.get(
      key,
      serviceCall,
      7500000, //2 hrs
      true
    );
  }

  /**
   *  Get full widget config based on WidgetID, AppID and tags.
   *
   * @param appID
   * @param widgetID
   * @param tags
   * @return
   * @memberof AppCacheService
   */
  getWidgetConfig(
    appID: number,
    processID: number,
    widgetID: number,
    tags: dcTag[],
    processInstanceID?: number,
    activityInstanceID?: number
  ): Observable<Widget> {
    const baseUrl = `App/${appID}/Widget/${widgetID}/Configuration`;
    const params = [
      processID && `processID=${processID}`,
      processInstanceID && `processInstanceID=${processInstanceID}`,
      activityInstanceID && `activityInstanceID=${activityInstanceID}`
    ].filter(Boolean);

    const url = params.length > 0 ? `${baseUrl}?${params.join('&')}` : baseUrl;

    return this.httpService.post<Widget>(url, tags, `getWidgetConfig${appID}_${widgetID}`);
  }

  getWorkflowApp() {
    return this.appCaches.find(app => app.appId == AppConstants.STATIC_WORKFLOW_APP_ID);
  }

  /**
   *  Get component settings based on ComponentName,  WidgetID, ComponentId and ComponentType.
   *
   * @param WidgetId
   * @param ComponentName
   * @param ComponentTypeId
   * @param ComponentId
   * @return
   * @memberof AppCacheService
   */
  getComponentSettings(settings: ComponentMetadataModel) {
    return this.httpService.post<ComponentSettingsModel>(`app/GetComponentSettings`, settings);
  }

  /**
   *  Saving Component Settings
   *
   */
  saveComponentSettings(settings: ComponentSettingsModel, isWellLevel: boolean) {
    if (isWellLevel) {
      return this.httpService.post<ComponentSettingsModel>(
        'app/SaveWellComponentSettings',
        settings
      );
    }
    return this.httpService.post<ComponentSettingsModel>('app/SaveComponentSettings', settings);
  }

  private getCacheKey(
    apiCallCacheKey: string,
    appCache: AppCache<App, AppRouteStateParams>,
    appID: number,
    tags: dcTag[]
  ): string {
    return `${apiCallCacheKey}_${appID}_${this.cacheService.generateCacheKeyForTags(
      tags.filter(
        tag => !this.commonService.isTagFlatTreeEquals(appCache.elementSelectorSelectedTagNode, tag)
      )
    )}_${appCache.elementSelectorSelectedTagNode.Abbreviation ||
      appCache.elementSelectorSelectedTagNode.ObjectName
      }`;
  }

  private parseWidgetsSupportedElements(widgets: Widget[]): void {
    if (!widgets) {
      return;
    }

    widgets.forEach(widget => {
      if (
        widget &&
        widget.SupportedElements &&
        (typeof widget.SupportedElements === 'string' || widget.SupportedElements instanceof String)
      ) {
        const parsedValue = JSON.parse(widget.SupportedElements as unknown as string);
        if (Array.isArray(parsedValue)) {
          widget.SupportedElements = parsedValue.map((element: string) => +element);
        }
      } else {
        widget.SupportedElements = null;
      }
    });
  }
}
